import i18n from '@/plugins/i18n'
import {
  COMPOSITION_CANONICAL_SECTION_PREFIX,
  COMPOSITION_CAS_NUMBER_KEY,
  COMPOSITION_CLASSIFICATION_KEY,
  COMPOSITION_COMPONENT_KEY,
  COMPOSITION_CONCENTRATION_KEY,
  COMPOSITION_EG_NUMBER_KEY,
  COMPOSITION_INDEX_NR_KEY,
  CANONICAL_LANGUAGE,
  ClassificationCanonicalsSuffixesEnum,
  RelatedItemsColumn,
  hasNumericSuffix,
} from './approval'
import { hasCanonicalSuffix } from '@/composables/useUtils'

export interface SDSSection {
  id: string
  index?: number
  name: string
  items?: SDSSection[]
}

export interface SDSFullSection extends SDSSection {
  subsections?: SDSFullSection[]
  formCanonicalOptions: Array<{ name: string; value: string; helpText?: string }>
}

export interface SDSFullSectionWithControls extends SDSFullSection {
  subsections?: SDSFullSectionWithControls[]
  show: boolean
  isChecked: boolean
  totalOfItems?: number
}

export interface SDSSectionWithControls extends SDSSection {
  show: boolean
}

export interface SDSKey {
  system: string
  valArea: string
  rating: string
  lang: string
  materialNumber: string
}

export const DEFAULT_SDS_KEY: SDSKey = {
  system: 'sys',
  valArea: 'reg_world',
  rating: 'public',
  lang: 'de',
  materialNumber: 'NONE',
}

export type DataObjectSource = 'dataobject' | 'approval'

export const SAP_RELATED_DATA_ASSETS: string =
  (window.ENV.SAP_RELATED_DATA_ASSETS && window.ENV.SAP_RELATED_DATA_ASSETS !== '**SAP_RELATED_DATA_ASSETS**'
    ? window.ENV.SAP_RELATED_DATA_ASSETS
    : import.meta.env.VITE_SAP_RELATED_DATA_ASSETS) || ''

export const DEFAULT_SEARCH_CANONICALS_FOR_SAP_REFERENCE: string =
  (window.ENV.DEFAULT_SEARCH_CANONICALS_FOR_SAP_REFERENCE &&
  window.ENV.DEFAULT_SEARCH_CANONICALS_FOR_SAP_REFERENCE !== '**DEFAULT_SEARCH_CANONICALS_FOR_SAP_REFERENCE**'
    ? window.ENV.DEFAULT_SEARCH_CANONICALS_FOR_SAP_REFERENCE
    : import.meta.env.VITE_DEFAULT_SEARCH_CANONICALS_FOR_SAP_REFERENCE) || ''

export const DEFAULT_SEARCH_CANONICALS_FOR_DATAOBJECT_UPDATE: string =
  (window.ENV.DEFAULT_SEARCH_CANONICALS_FOR_DATAOBJECT_UPDATE &&
  window.ENV.DEFAULT_SEARCH_CANONICALS_FOR_DATAOBJECT_UPDATE !== '**DEFAULT_SEARCH_CANONICALS_FOR_DATAOBJECT_UPDATE**'
    ? window.ENV.DEFAULT_SEARCH_CANONICALS_FOR_DATAOBJECT_UPDATE
    : import.meta.env.VITE_DEFAULT_SEARCH_CANONICALS_FOR_DATAOBJECT_UPDATE) || ''

export const DEFAULT_SEARCH_CANONICALS_FOR_COMPOSITION_COMPONENTS: string =
  (window.ENV.DEFAULT_SEARCH_CANONICALS_FOR_COMPOSITION_COMPONENTS &&
  window.ENV.DEFAULT_SEARCH_CANONICALS_FOR_COMPOSITION_COMPONENTS !==
    '**DEFAULT_SEARCH_CANONICALS_FOR_COMPOSITION_COMPONENTS**'
    ? window.ENV.DEFAULT_SEARCH_CANONICALS_FOR_COMPOSITION_COMPONENTS
    : import.meta.env.VITE_DEFAULT_SEARCH_CANONICALS_FOR_COMPOSITION_COMPONENTS) || ''

export const SEARCH_DATA_ASSETS_FOR_SAP_REFERENCE: string =
  (window.ENV.SEARCH_DATA_ASSETS_FOR_SAP_REFERENCE &&
  window.ENV.SEARCH_DATA_ASSETS_FOR_SAP_REFERENCE !== '**SEARCH_DATA_ASSETS_FOR_SAP_REFERENCE**'
    ? window.ENV.SEARCH_DATA_ASSETS_FOR_SAP_REFERENCE
    : import.meta.env.VITE_SEARCH_DATA_ASSETS_FOR_SAP_REFERENCE) || ''

export const SEARCH_DATA_ASSETS_FOR_DATAOBJECT_UPDATE: string =
  (window.ENV.SEARCH_DATA_ASSETS_FOR_DATAOBJECT_UPDATE &&
  window.ENV.SEARCH_DATA_ASSETS_FOR_DATAOBJECT_UPDATE !== '**SEARCH_DATA_ASSETS_FOR_DATAOBJECT_UPDATE**'
    ? window.ENV.SEARCH_DATA_ASSETS_FOR_DATAOBJECT_UPDATE
    : import.meta.env.VITE_SEARCH_DATA_ASSETS_FOR_DATAOBJECT_UPDATE) || ''

export const SEARCH_DATA_ASSETS_FOR_COMPOSITION_COMPONENTS: string =
  (window.ENV.SEARCH_DATA_ASSETS_FOR_COMPOSITION_COMPONENTS &&
  window.ENV.SEARCH_DATA_ASSETS_FOR_COMPOSITION_COMPONENTS !== '**SEARCH_DATA_ASSETS_FOR_COMPOSITION_COMPONENTS**'
    ? window.ENV.SEARCH_DATA_ASSETS_FOR_COMPOSITION_COMPONENTS
    : import.meta.env.VITE_SEARCH_DATA_ASSETS_FOR_COMPOSITION_COMPONENTS) || ''

export const SEARCH_DATA_ASSETS_FOR_HAZARD_INDUCERS: string =
  (window.ENV.SEARCH_DATA_ASSETS_FOR_HAZARD_INDUCERS &&
  window.ENV.SEARCH_DATA_ASSETS_FOR_HAZARD_INDUCERS !== '**SEARCH_DATA_ASSETS_FOR_HAZARD_INDUCERS**'
    ? window.ENV.SEARCH_DATA_ASSETS_FOR_HAZARD_INDUCERS
    : import.meta.env.VITE_SEARCH_DATA_ASSETS_FOR_HAZARD_INDUCERS) || ''

export const SPECIAL_CANONICAL_TENSOR_ITEM = 'model-tensor_SEC_9'

export const CLASSIFICATION_SECTION_PREFIX = 'SEC_2_1'

export const SDS_CANONICAL_REG_BASE_NAME = 'SEC_2_2.REG_BASE'

export const CLASSIFICATION_CANONICAL_NAME = 'SEC_2_1.CLASSIFICATION'

export const CLASSIFICATION_PREFIX_KEY = 'CLASSIFICATION_'

/*
  Classification canonical convention:
  - Legacy (no region): SEC_2_1.CLASSIFICATION_<Property>_<Suffix>
  - With region:        SEC_2_1.<Region Code>_CLASSIFICATION_<Property>_<Suffix>
  e.g. SEC_2_1.GB_CLASSIFICATION_HAZARDCLASS_0
*/

// Extracts the region code from a classification canonical, '' means the legacy/no-region format
export function getClassificationRegion(canonical: string): string {
  const sectionItem = canonical.split('.')[1]
  if (!sectionItem) return ''
  if (sectionItem.startsWith(CLASSIFICATION_PREFIX_KEY)) return ''
  const regionIndex = sectionItem.indexOf(CLASSIFICATION_PREFIX_KEY)
  if (regionIndex <= 0) return ''
  return sectionItem.slice(0, regionIndex).replace(/_{1,}$/, '')
}

// Returns the region-aware base canonical name (without property nor suffix)
export function getClassificationCanonicalName(region?: string): string {
  if (!region) return CLASSIFICATION_CANONICAL_NAME
  const canonicalWithoutSection = CLASSIFICATION_CANONICAL_NAME.split('.')[1] || ''
  return `${CLASSIFICATION_SECTION_PREFIX}.${region}_${canonicalWithoutSection}`
}

// Returns a full classification canonical (property + number suffix) using a config canonicalName that already includes the CLASSIFICATION_ prefix
export function getClassificationCanonical(
  region: string | undefined,
  canonicalName: string,
  numberSuffix: number,
): string {
  const property = canonicalName.replace(CLASSIFICATION_PREFIX_KEY, '')
  return `${getClassificationCanonicalName(region)}_${property}_${numberSuffix}`
}

// Returns the canonical without the region segment (regionless), e.g. SEC_2_1.GB_CLASSIFICATION_HAZARDCLASS_0 -> SEC_2_1.CLASSIFICATION_HAZARDCLASS_0
export function getClassificationCanonicalWithoutRegion(canonical: string): string {
  const region = getClassificationRegion(canonical)
  if (!region) return canonical
  return canonical.replace(`${CLASSIFICATION_SECTION_PREFIX}.${region}_`, `${CLASSIFICATION_SECTION_PREFIX}.`)
}

// Returns true when the fieldName is a classification canonical with numeric suffix (legacy no-region or any region variant)
export function isClassificationCanonical(fieldName: string): boolean {
  const [section = '', sectionItem = ''] = fieldName.split('.')
  if (section !== CLASSIFICATION_SECTION_PREFIX) return false
  if (!sectionItem.includes(CLASSIFICATION_PREFIX_KEY)) return false
  return hasNumericSuffix(fieldName)
}

// Returns the DOM id of the classification table for a given region ('' or undefined = legacy no-region table)
export function getClassificationTableId(region?: string): string {
  return `table-classification-${region || 'default'}`
}

export function isMatchingFieldName(fieldName: string, name: string): boolean {
  const isClassification = isClassificationCanonical(fieldName)
  if (isClassification) {
    return getClassificationCanonicalWithoutRegion(fieldName).startsWith(name)
  } else {
    return hasCanonicalSuffix(fieldName) ? fieldName.startsWith(name) : fieldName === name
  }
}

export const COMPOSITION_SECTION_PREFIX = 'SEC_3_2'

export const COMPOSITION_PREFIX_KEY = 'COMPOSITION_'

export const HAZARD_STATEMENT_PREFIX_KEY = `${COMPOSITION_PREFIX_KEY}HAZ_STATEMENT`

export const ITEMS_PREFIXES_TO_GROUP = [COMPOSITION_PREFIX_KEY, CLASSIFICATION_PREFIX_KEY]

export const SOURCE_SYSTEM_OPTIONS = [{ name: 'sys', value: 'sys' }]

export const RATING_OPTIONS = [{ name: 'public', value: 'public' }]

export const VALIDITY_AREA_OPTIONS = [{ name: 'reg world', value: 'reg_world' }]

export const LANGUAGE_OPTIONS = [
  { name: 'EN', value: 'en' },
  { name: 'DE', value: 'de' },
]

export const SPECIFICATION_NUMBER_CANONICAL_SUFFIX = 'NUMBER'

export const SDS_NON_SDS_PREFIX = 'SEC_0.NONSDS_'
export const SDS_CANONICAL_ACCESS_LEVEL = 'SEC_0_1.ACCESS_LEVEL'
export const SDS_CANONICAL_TYPE = 'SEC_0_1.TYPE'
export const SDS_CANONICAL_FORMAT = 'SEC_0_1.FORMAT'
export const SDS_CANONICAL_COUNTRY = 'SEC_0_1.COUNTRY'
export const SDS_CANONICAL_REVISION_DATE = 'SEC_0_1.REV_DATE'
export const SDS_CANONICAL_PRINT_DATE = 'SEC_0_1.PRINT_DATE'
export const SDS_CANONICAL_RECEIVE_DATE = 'SEC_0_1.RECEIVE_DATE'
export const SDS_CANONICAL_VALIDITY_DATE_CONFIRMATION = 'SEC_0_1.VALIDITY_DATE_CONFIRMATION'
export const SDS_CANONICAL_SUPPLIER = 'SEC_0_1.SUPPLIER'
export const SDS_CANONICAL_SOURCE = 'SEC_0_1.SOURCE'
export const SDS_CANONICAL_ADDITIONAL_INFORMATION = 'SEC_0_1.ADDITIONAL_INFORMATION'
export const SDS_CANONICAL_DISTRIBUTION_TO_HSA = 'SEC_0_1.DISTRIBUTION_TO_HSA'
export const SDS_CANONICAL_UN_UA_NUMBER = 'SEC_0_1.UN_UA_NUMBER'
export const SDS_CANONICAL_COMMENT = 'SEC_0_1.COMMENT'
export const SDS_CANONICAL_DATE_OF_UPLOAD = 'SEC_0_1.DATE_OF_UPLOAD'
export const SDS_CANONICAL_CREATED_BY = 'SEC_0_1.CREATED_BY'
export const SDS_CANONICAL_LAST_CHANGE_ON = 'SEC_0_1.LAST_CHANGE_ON'
export const SDS_CANONICAL_LAST_CHANGE_BY = 'SEC_0_1.LAST_CHANGE_BY'
export const SIMULATION_TYPE_IDENTIFIER = 'SEC_0.TYPE_OF_CHANGE'
export const SDS_CANONICAL_DECIMAL_NOTATION = 'SEC_0_1.NOTATION'
export const SDS_CANONICAL_NAME = 'SEC_1_1.NAME'
export const SDS_CANONICAL_CAS = 'SEC_1_1.CAS'
export const SDS_CANONICAL_INDEX_NR = 'SEC_1_1.INDEX_NR'
export const SDS_CANONICAL_EG_NO = 'SEC_1_1.EG_NR'
export const SDS_CANONICAL_PRODUCT_NUMBER = 'SEC_1_1.NUMBER'
export const SDS_CANONICAL_MANUFACTURER = 'SEC_1_3.MANUFACTURER'
export const SDS_CANONICAL_DATA_OWNER = 'SEC_1_3.DATA_OWNER'
export const SDS_CANONICAL_PICTOGRAM = 'SEC_2_2_1.SYMBOL'
export const SDS_CANONICAL_PICTOGRAM_LABELLING = 'SEC_2_2_2.SIGNAL'
export const SDS_CANONICAL_EUH_PHRASE = 'SEC_2_2.EUH_STAT'
export const SDS_CANONICAL_EMERGENCY_TELEPHONE = 'SEC_1_4.EMERGENCY_TELEPHONE'
export const SDS_CANONICAL_SPEC_NAME = 'SEC_0.SPEC_NAME'
export const SDS_CANONICAL_SPEC_ID = 'SEC_0.SPEC_ID'
export const SDS_CANONICAL_THRESHOLD = 'SEC_0.THRESHOLD'
export const SDS_CANONICAL_TEXT = 'SEC_0.TEXT'
export const SDS_CANONICAL_RECORD_NUMBER = 'SEC_0.RECORD_NO'
export const SDS_CANONICAL_DENSITY_MID = 'SEC_0.SAMPLE_MID_DENSITY'
export const SDS_CANONICAL_DENSITY_LOW = 'SEC_0.SAMPLE_LOW_DENSITY'
export const SDS_CANONICAL_SIGNAL_NA = 'SEC_0.SAMPLE_NA_SIGNAL'
export const SDS_CANONICAL_CLASSIFICATION_HAZARDCLASS = `${CLASSIFICATION_SECTION_PREFIX}.${ClassificationCanonicalsSuffixesEnum.HazardClass}`
export const SDS_CANONICAL_CLASSIFICATION_CATEGORY = `${CLASSIFICATION_SECTION_PREFIX}.${ClassificationCanonicalsSuffixesEnum.Category}`
export const SDS_CANONICAL_CLASSIFICATION_ROUTE_OF_EXPOSURE = `${CLASSIFICATION_SECTION_PREFIX}.${ClassificationCanonicalsSuffixesEnum.RouteOfExposure}`
export const SDS_CANONICAL_CLASSIFICATION_TARGET_ORGAN = `${CLASSIFICATION_SECTION_PREFIX}.${ClassificationCanonicalsSuffixesEnum.Organ}`
export const SDS_CANONICAL_CLASSIFICATION_HAZARD_STATEMENT = `${CLASSIFICATION_SECTION_PREFIX}.${ClassificationCanonicalsSuffixesEnum.HazardStatement}`
export const SDS_CANONICAL_CLASSIFICATION_ADDITIONAL_INFO = `${CLASSIFICATION_SECTION_PREFIX}.${ClassificationCanonicalsSuffixesEnum.AddInfo}`
export const SDS_CANONICAL_CLASSIFICATION_REMARKS = `${CLASSIFICATION_SECTION_PREFIX}.${ClassificationCanonicalsSuffixesEnum.Remark}`
export const SDS_CANONICAL_CLASSIFICATION_REG_BASE = `${CLASSIFICATION_SECTION_PREFIX}.${ClassificationCanonicalsSuffixesEnum.RegulatoryBase}`
export const SDS_CANONICAL_CLASSIFICATION_REG_LIST = `${CLASSIFICATION_SECTION_PREFIX}.${ClassificationCanonicalsSuffixesEnum.RegulatoryList}`
export const SDS_CANONICAL_CLASSIFICATION_HCODE = `${CLASSIFICATION_SECTION_PREFIX}.${ClassificationCanonicalsSuffixesEnum.HCode}`
export const SDS_CANONICAL_COMPOSITION_CONCENTRATION = `${COMPOSITION_CANONICAL_SECTION_PREFIX}.${COMPOSITION_CONCENTRATION_KEY}`
export const SDS_CANONICAL_COMPOSITION_CAS_NUMBER = `${COMPOSITION_CANONICAL_SECTION_PREFIX}.${COMPOSITION_CAS_NUMBER_KEY}`
export const SDS_CANONICAL_CONCENTRATION = 'SEC_12_2.CONCENTRATION'
export const SEC_CANONICAL_CONCENTRATION_SAIFTY_BASE = 'SEC_12_2.CONCENTRATION_SAIFTY_BASE'
export const SEC_CANONICAL_CONCENTRATION_SAIFTY_LIMIT = 'SEC_12_2.CONCENTRATION_SAIFTY_LIMIT'
export const SEC_CANONICAL_CONCENTRATION_SAIFTY_CLASS = 'SEC_12_2.CONCENTRATION_SAIFTY_CLASS'
export const SEC_CANONICAL_CONCENTRATION_SAIFTY_CATEGORY = 'SEC_12_2.CONCENTRATION_SAIFTY_CATEGORY'
export const SEC_CANONICAL_CONCENTRATION_SAIFTY_ROUTE_EXPO = 'SEC_12_2.CONCENTRATION_SAIFTY_ROUTE_EXPO'
export const SEC_CANONICAL_CONCENTRATION_SAIFTY_ORGAN = 'SEC_12_2.CONCENTRATION_SAIFTY_ORGAN'
export const SEC_CANONICAL_CONCENTRATION_SAIFTY_HAZ_STATEMENT = 'SEC_12_2.CONCENTRATION_SAIFTY_HAZ_STATEMENT'
export const SEC_CANONICAL_CONCENTRATION_SAIFTY_ADD_INFO = 'SEC_12_2.CONCENTRATION_SAIFTY_ADD_INFO'
export const SDS_CANONICAL_M_FACTOR = 'SEC_12_3.M_FACTOR'
export const SEC_CANONICAL_M_FACTOR_SAIFTY_BASE = 'SEC_12_3.M_FACTOR_SAIFTY_BASE'
export const SEC_CANONICAL_M_FACTOR_SAIFTY_M_FACTOR = 'SEC_12_3.M_FACTOR_SAIFTY_M_FACTOR'
export const SEC_CANONICAL_M_FACTOR_SAIFTY_MFACT_CHRONIC = 'SEC_12_3.M_FACTOR_SAIFTY_MFACT_CHRONIC'
export const SEC_CANONICAL_M_FACTOR_SAIFTY_MFACT_SOIL = 'SEC_12_3.M_FACTOR_SAIFTY_MFACT_SOIL'
export const SEC_CANONICAL_M_FACTOR_SAIFTY_MFACT_VERT = 'SEC_12_3.M_FACTOR_SAIFTY_MFACT_VERT'
export const SEC_CANONICAL_M_FACTOR_SAIFTY_MFACT_INVERT = 'SEC_12_3.M_FACTOR_SAIFTY_MFACT_INVERT'
export const SEC_CANONICAL_M_FACTOR_SAIFTY_REMARK = 'SEC_12_3.M_FACTOR_SAIFTY_REMARK'
export const SDS_CANONICAL_LAND_TRANSPORT = 'SEC_14_1.LT_UNNUMBER'
export const SDS_CANONICAL_WATER_HAZARD_CLASS = 'SEC_15_1_5.WGK'
export const SEC_CANONICAL_WGK_SAIFTY_BASE = 'SEC_15_1_5.WGK_SAIFTY_BASE'
export const SEC_CANONICAL_WGK_SAIFTY_HAZARD_CLASS = 'SEC_15_1_5.WGK_SAIFTY_HAZARD_CLASS'
export const SEC_CANONICAL_WGK_SAIFTY_IDENT_NUM = 'SEC_15_1_5.WGK_SAIFTY_IDENT_NUM'
export const SEC_CANONICAL_NON_DG = 'SEC_14.IS_NON_DG'
export const HSE_RELEVANT_CANONICAL = 'SEC_0_1.DISTRIBUTION_TO_HSA'

export const SDS_CLASSIFICATION_CANONICAL_LIST = [
  SDS_CANONICAL_CLASSIFICATION_HAZARDCLASS,
  SDS_CANONICAL_CLASSIFICATION_CATEGORY,
  SDS_CANONICAL_CLASSIFICATION_ROUTE_OF_EXPOSURE,
  SDS_CANONICAL_CLASSIFICATION_TARGET_ORGAN,
  SDS_CANONICAL_CLASSIFICATION_HAZARD_STATEMENT,
  SDS_CANONICAL_CLASSIFICATION_ADDITIONAL_INFO,
  SDS_CANONICAL_CLASSIFICATION_REMARKS,
  SDS_CANONICAL_CLASSIFICATION_REG_BASE,
  SDS_CANONICAL_CLASSIFICATION_REG_LIST,
  SDS_CANONICAL_CLASSIFICATION_HCODE,
]

// Note: Do not change the order
export const SDS_SPECIFICATION_HEADER_ITEMS = [
  SDS_CANONICAL_ACCESS_LEVEL,
  SDS_CANONICAL_TYPE,
  SDS_CANONICAL_FORMAT,
  SDS_CANONICAL_COUNTRY,
  CANONICAL_LANGUAGE,
  SDS_CANONICAL_REVISION_DATE,
  SDS_CANONICAL_PRINT_DATE,
  SDS_CANONICAL_RECEIVE_DATE,
  SDS_CANONICAL_VALIDITY_DATE_CONFIRMATION,
  SDS_CANONICAL_SUPPLIER,
  SDS_CANONICAL_ADDITIONAL_INFORMATION,
  SDS_CANONICAL_DISTRIBUTION_TO_HSA,
  SDS_CANONICAL_UN_UA_NUMBER,
  SDS_CANONICAL_COMMENT,
  SDS_CANONICAL_DATE_OF_UPLOAD,
  SDS_CANONICAL_CREATED_BY,
  SDS_CANONICAL_LAST_CHANGE_ON,
  SDS_CANONICAL_LAST_CHANGE_BY,
]

export const ASSESSMENT_CLASSIFICATION_COLUMNS: RelatedItemsColumn[] = [
  {
    // eslint-disable-next-line
    // @ts-ignore
    name: i18n.global.t('Class'),
    canonicalPreffix: ClassificationCanonicalsSuffixesEnum.HazardClass,
    colClasses: 'col-2',
  },
  {
    name: i18n.global.t('Category'),
    canonicalPreffix: ClassificationCanonicalsSuffixesEnum.Category,
    colClasses: 'col-1',
  },
  {
    name: i18n.global.t('Additional information'),
    canonicalPreffix: ClassificationCanonicalsSuffixesEnum.AddInfo,
    colClasses: 'col-2',
  },
  {
    name: i18n.global.t('Organ'),
    canonicalPreffix: ClassificationCanonicalsSuffixesEnum.Organ,
    colClasses: 'col-2',
  },
  {
    name: i18n.global.t('Route of exposure'),
    canonicalPreffix: ClassificationCanonicalsSuffixesEnum.RouteOfExposure,
    colClasses: 'col-2',
  },
  {
    name: i18n.global.t('Remark'),
    canonicalPreffix: ClassificationCanonicalsSuffixesEnum.Remark,
    colClasses: 'col-2',
  },
  {
    name: i18n.global.t('Hazard statements'),
    canonicalPreffix: ClassificationCanonicalsSuffixesEnum.HazardStatement,
    colClasses: 'col-2',
  },
  {
    name: 'HCODE',
    canonicalPreffix: ClassificationCanonicalsSuffixesEnum.HCode,
    colClasses: 'col-2',
  },
  {
    name: i18n.global.t('Regulatory Base'),
    canonicalPreffix: ClassificationCanonicalsSuffixesEnum.RegulatoryBase,
    colClasses: 'col-2',
  },
  {
    name: i18n.global.t('Regulatory List'),
    canonicalPreffix: ClassificationCanonicalsSuffixesEnum.RegulatoryList,
    colClasses: 'col-2',
  },
]

export const HAZARD_CLASSIFICATION_FIRST_LEVEL_ID = 'SEC_2'
export const COMPOSITION_FIRST_LEVEL_ID = 'SEC_3'
export const PHYSICAL_AND_CHEMICAL_PROPERTIES_FIRST_LEVEL_ID = 'SEC_9'
// TODO: This list should come from the backend, for now it's hardcoded and it's only for lanxess
export const LANXESS_CANONICALS_FOR_NOTATION_TRANSFORM = [
  'SEC_3_1_5.MOLAR',
  'SEC_7_2.STORAGE_DUR_MAX',
  'SEC_7_2.STORAGE_TEMP_REC',
  'SEC_8_1.DNEL',
  'SEC_8_1.PNEC',
  'SEC_9_1.AUTOIG_TEMP',
  'SEC_9_1.BOILING_POINT',
  'SEC_9_2.BULK_DENISTY',
  'SEC_9_2.BURNING_RATE',
  'SEC_9_1.DECOMP_TEMP',
  'SEC_9_1.DENSITY',
  'SEC_9_2.DISSOCIATION_CONSTANT',
  'SEC_9_1.EVAP_RATE',
  'SEC_9_1.FLASH_POINT',
  'SEC_9_1.GRANULOMETRY',
  'SEC_9_2.IGNITION_TEMP',
  'SEC_9_2.KIN_VISCOSITY',
  'SEC_9_1.EXPL_LIMIT_LO',
  'SEC_9_1.MELTING_POINT',
  'SEC_9_2.IGNITION_ENERG_MIN',
  'SEC_9_2.OTHER_SOLUBILITY',
  'SEC_9_2.PARTICLE_SIZE',
  'SEC_9_1.PARTITION_COEF',
  'SEC_9_1.PEROXIDES',
  'SEC_9_1.PH',
  'SEC_9_1.RELATIVE_DENSITY',
  'SEC_9_1.REL_VAP_DENS',
  'SEC_9_1.SADT',
  'SEC_9_1.SELF_IGNITION',
  'SEC_9_1.SOLUBILITY',
  'SEC_9_1.STATE_OF_MATTER',
  'SEC_9_2.SURFACE_TENSION',
  'SEC_9_1.EXPL_LIMIT_HI',
  'SEC_9_1.VAP_PRES',
  'SEC_9_1.DYN_VISCOSITY',
  'SEC_9_1.WATER_SOLUBILITY',
  'SEC_10_4_1.COND_PAR',
  'SEC_11_1.INHA_TOX',
  'SEC_11_1.ORAL_TOX',
  'SEC_11_1.OTHER_TOX',
  'SEC_11_1.REPEAT_DOSE_TOX',
  'SEC_11_1.SKIN_TOX',
  'SEC_12_3.BIOACC_POT',
  'SEC_12_3.BIOACC',
  'SEC_12_2.BIOCHEM_OXY',
  'SEC_12_2.BIODEG',
  'SEC_12_2.DOC',
  'SEC_12_2.VOC',
  'SEC_12_4.DISTR_ENV_COMP',
  'SEC_12_6.HENRY_CONST',
  'SEC_12_3.M_FACTOR',
  'SEC_12_4.MOBILITY',
  'SEC_12_2.RATIO_BOD_COD',
  'SEC_12_2.RATIO_BOD_THBOD',
  'SEC_12_6.SURFACE_TENS',
  'SEC_12_2.THEO_OXY',
  'SEC_12_1.TOX_DAPHN_CHRON',
  'SEC_12_1.TOX_FISH_CHRON',
  'SEC_12_1.TOX_ALGAE',
  'SEC_12_1.TOX_BACTERIA',
  'SEC_12_1.TOX_DAPHN',
  'SEC_12_1.TOX_FISH',
  'SEC_13.WASTE_TREAT_PACK',
  SDS_CANONICAL_WATER_HAZARD_CLASS,
]

export const SPECIFICATION_HEADER_SECTION_OBJECT = {
  id: 'SEC_0',
  index: 0,
  // eslint-disable-next-line
  // @ts-ignore
  name: i18n.global.t('Specification Header'),
  items: [],
}

export const SDS_TREE: SDSSection[] = [
  ...(window.ENV.SHOW_SECTION_ZERO_IN_THE_SDS_PASSPORTS ? [SPECIFICATION_HEADER_SECTION_OBJECT] : []),
  {
    id: 'SEC_1',
    index: 1,
    // eslint-disable-next-line
    // @ts-ignore
    name: i18n.global.t('Identification'),
    items: [
      { id: 'SEC_1_1', name: i18n.global.t('Product Identifier') },
      {
        id: 'SEC_1_2',
        name: i18n.global.t('Relevant identified uses of the substance or mixture and uses advised against'),
      },
      { id: 'SEC_1_3', name: i18n.global.t('Details of the supplier of the safety data sheet') },
      { id: 'SEC_1_4', name: i18n.global.t('Emergency telephone number') },
    ],
  },
  {
    id: HAZARD_CLASSIFICATION_FIRST_LEVEL_ID,
    index: 2,
    name: i18n.global.t('Hazards identification'),
    items: [
      { id: 'SEC_2_1', name: i18n.global.t('Classification of the substance or mixture') },
      { id: 'SEC_2_2', name: i18n.global.t('Label elements') },
      { id: 'SEC_2_3', name: i18n.global.t('Other hazards') },
    ],
  },
  {
    id: COMPOSITION_FIRST_LEVEL_ID,
    index: 3,
    name: i18n.global.t('Composition/information on ingredients'),
    items: [
      { id: 'SEC_3_1', name: i18n.global.t('Substances') },
      { id: 'SEC_3_2', name: i18n.global.t('Mixtures') },
    ],
  },
  {
    id: 'SEC_4',
    index: 4,
    name: i18n.global.t('First aid measures'),
    items: [
      { id: 'SEC_4_1', name: i18n.global.t('Description of first aid measures') },
      { id: 'SEC_4_2', name: i18n.global.t('Most important symptoms and effects, both acute and delayed') },
      {
        id: 'SEC_4_3',
        name: i18n.global.t('Indication of any immediate medical attention and special treatment needed'),
      },
    ],
  },
  {
    id: 'SEC_5',
    index: 5,
    name: i18n.global.t('Fire-fighting measures'),
    items: [
      { id: 'SEC_5_1', name: i18n.global.t('Extinguishing media') },
      { id: 'SEC_5_2', name: i18n.global.t('Special hazards arising from the substance or mixture') },
      { id: 'SEC_5_3', name: i18n.global.t('Advice for firefighters') },
    ],
  },
  {
    id: 'SEC_6',
    index: 6,
    name: i18n.global.t('Accidental release measures'),
    items: [
      { id: 'SEC_6_1', name: i18n.global.t('Personal precautions, protective equipment and emergency procedures') },
      { id: 'SEC_6_2', name: i18n.global.t('Environmental precautions') },
      { id: 'SEC_6_3', name: i18n.global.t('Methods and material for containment and cleaning up') },
      { id: 'SEC_6_4', name: i18n.global.t('Reference to other sections') },
    ],
  },
  {
    id: 'SEC_7',
    index: 7,
    name: i18n.global.t('Handling and storage'),
    items: [
      { id: 'SEC_7_1', name: i18n.global.t('Precautions for safe handling') },
      { id: 'SEC_7_2', name: i18n.global.t('Conditions for safe storage, including any incompatibilities') },
      { id: 'SEC_7_3', name: i18n.global.t('Specific end use(s)') },
    ],
  },
  {
    id: 'SEC_8',
    index: 8,
    name: i18n.global.t('Exposure controls/personal protection'),
    items: [
      { id: 'SEC_8_1', name: i18n.global.t('Control parameters') },
      { id: 'SEC_8_2', name: i18n.global.t('Exposure controls') },
    ],
  },
  {
    id: PHYSICAL_AND_CHEMICAL_PROPERTIES_FIRST_LEVEL_ID,
    index: 9,
    name: i18n.global.t('Physical and chemical properties'),
    items: [
      { id: 'SEC_9_1', name: i18n.global.t('Information on basic physical and chemical properties') },
      { id: 'SEC_9_2', name: i18n.global.t('Other information') },
    ],
  },
  {
    id: 'SEC_10',
    index: 10,
    name: i18n.global.t('Stability and reactivity'),
    items: [
      { id: 'SEC_10_1', name: i18n.global.t('Reactivity') },
      { id: 'SEC_10_2', name: i18n.global.t('Chemical stability') },
      { id: 'SEC_10_3', name: i18n.global.t('Possibility of hazardous reactions') },
      { id: 'SEC_10_4', name: i18n.global.t('Conditions to avoid') },
      { id: 'SEC_10_5', name: i18n.global.t('Incompatible materials') },
      { id: 'SEC_10_6', name: i18n.global.t('Hazardous decomposition products') },
    ],
  },
  {
    id: 'SEC_11',
    index: 11,
    name: i18n.global.t('Toxicological information'),
    items: [
      { id: 'SEC_11_1', name: i18n.global.t('Information on toxicological effects') },
      { id: 'SEC_11_2', name: i18n.global.t('Information on other hazards') },
    ],
  },
  {
    id: 'SEC_12',
    index: 12,
    name: i18n.global.t('Ecological information'),
    items: [
      { id: 'SEC_12_1', name: i18n.global.t('Toxicity') },
      { id: 'SEC_12_2', name: i18n.global.t('Persistence and degradability') },
      { id: 'SEC_12_3', name: i18n.global.t('Bioaccumulative potential') },
      { id: 'SEC_12_4', name: i18n.global.t('Mobility in soil') },
      { id: 'SEC_12_5', name: i18n.global.t('Results of PBT and vPvB assessment') },
      { id: 'SEC_12_6', name: i18n.global.t('Endocrine disrupting properties') },
      { id: 'SEC_12_7', name: i18n.global.t('Other adverse effects') },
    ],
  },
  {
    id: 'SEC_13',
    index: 13,
    name: i18n.global.t('Disposal considerations'),
    items: [{ id: 'SEC_13_1', name: i18n.global.t('Waste treatment methods') }],
  },
  {
    id: 'SEC_14',
    index: 14,
    name: i18n.global.t('Transport information'),
    items: [
      { id: 'SEC_14_1', name: i18n.global.t('UN number') },
      { id: 'SEC_14_2', name: i18n.global.t('UN proper shipping name') },
      { id: 'SEC_14_3', name: i18n.global.t('Transport hazard class(es)') },
      { id: 'SEC_14_4', name: i18n.global.t('Packing group') },
      { id: 'SEC_14_5', name: i18n.global.t('Environmental hazards') },
      { id: 'SEC_14_6', name: i18n.global.t('Special precautions for user') },
      {
        id: 'SEC_14_7',
        name: i18n.global.t('Transport in bulk according to Annex II of MARPOL73/78 and the IBC Code'),
      },
    ],
  },
  {
    id: 'SEC_15',
    index: 15,
    name: i18n.global.t('Regulatory information'),
    items: [
      {
        id: 'SEC_15_1',
        name: i18n.global.t(
          'Safety, health and environmental regulations/legislation specific for the substance or mixture',
        ),
      },
      { id: 'SEC_15_2', name: i18n.global.t('Chemical safety assessment') },
    ],
  },
  {
    id: 'SEC_16',
    index: 16,
    name: i18n.global.t('Information'),
    items: [
      {
        id: 'SEC_16_1',
        name: i18n.global.t('Other Information'),
      },
      {
        id: 'SEC_16_2',
        name: i18n.global.t('CLP Classification Comments'),
      },
      {
        id: 'SEC_16_3',
        name: i18n.global.t('Educational Recommendations'),
      },
      {
        id: 'SEC_16_4',
        name: i18n.global.t('Further Information'),
      },
      {
        id: 'SEC_16_5',
        name: i18n.global.t('Abbreviations And Acronyms Used'),
      },
      {
        id: 'SEC_16_6',
        name: i18n.global.t('Revision Comments'),
      },
      {
        id: 'SEC_16_7',
        name: i18n.global.t('Further Information'),
      },
    ],
  },
]

export const TDS_CANONICAL_NAME = 'PRODUCT_INFORMATION.NAME'

export const TDS_TREE: SDSSection[] = [
  {
    id: 'PRODUCT_INFORMATION',
    index: 1,
    name: i18n.global.t('Product Information'),
  },
  {
    id: 'PRODUCT_PROPERTIES',
    index: 2,
    name: i18n.global.t('Product properties'),
    items: [
      {
        id: 'PRODUCT_PROPERTIES_GENERAL',
        name: i18n.global.t('General'),
      },
      { id: 'PRODUCT_PROPERTIES_SINGLE_COMPONENT_A', name: i18n.global.t('Component A') },
      { id: 'PRODUCT_PROPERTIES_SINGLE_COMPONENT_B', name: i18n.global.t('Component B') },
      { id: 'PRODUCT_PROPERTIES_MIXED_PRODUCT', name: i18n.global.t('Mixed product') },
      {
        id: 'PRODUCT_PROPERTIES_MIXED_CURED_PRODUCT_THERMAL',
        name: i18n.global.t('Thermal properties'),
      },
      {
        id: 'PRODUCT_PROPERTIES_MIXED_CURED_PRODUCT_MECHANICAL',
        name: i18n.global.t('Mechanical properties'),
      },
      {
        id: 'PRODUCT_PROPERTIES_MIXED_CURED_PRODUCT_ELECTRICAL',
        name: i18n.global.t('Electrical properties'),
      },
    ],
  },
  {
    id: 'PRODUCT_PROCESSING_INSTRUCTIONS',
    index: 3,
    name: i18n.global.t('Processing instructions'),
  },
]

export const DMP_TREE: SDSSection[] = [
  {
    id: 'SEC_1',
    index: 1,
    // eslint-disable-next-line
    // @ts-ignore
    name: i18n.global.t('Identification'),
    items: [{ id: 'SEC_1_1', name: i18n.global.t('Product Identifier') }],
  },
  {
    id: 'MANUFACTURER',
    index: 2,
    name: i18n.global.t('Manufacturer'),
    items: [],
  },
  {
    id: COMPOSITION_FIRST_LEVEL_ID,
    index: 3,
    name: i18n.global.t('Composition/information on ingredients'),
    items: [
      { id: 'SEC_3_1', name: i18n.global.t('Substances') },
      { id: 'SEC_3_2', name: i18n.global.t('Mixtures') },
    ],
  },
  {
    id: 'PRODUCT',
    index: 4,
    name: i18n.global.t('Product'),
    items: [],
  },
  {
    id: 'PCF',
    index: 5,
    name: i18n.global.t('Product carbon footprint'),
    items: [],
  },
  {
    id: 'CIRCULARITY',
    index: 6,
    name: i18n.global.t('Circularity'),
    items: [],
  },
  {
    id: 'WATER',
    index: 7,
    name: i18n.global.t('Water'),
    items: [],
  },
  {
    id: 'SUSTAINABILITY',
    index: 8,
    name: i18n.global.t('Sustainability'),
    items: [],
  },
]

export const COMPANY_CERTIFICATE_TREE: SDSSection[] = [
  { id: 'BUSINESSPARTNERNUMBER', index: 1, name: i18n.global.t('Business partner number') },
  { id: 'REGISTRATIONNUMBER', index: 2, name: i18n.global.t('Registration number') },
  { id: 'UPLOADER', index: 3, name: i18n.global.t('Uploader') },
  { id: 'DOCUMENT', index: 4, name: i18n.global.t('Document') },
  { id: 'VALIDATOR', index: 5, name: i18n.global.t('Validator') },
  { id: 'VALIDUNTIL', index: 6, name: i18n.global.t('Valid until') },
  { id: 'VALIDFROM', index: 7, name: i18n.global.t('Valid from') },
  { id: 'TRUSTLEVEL', index: 8, name: i18n.global.t('Trust level') },
  { id: 'TYPE', index: 9, name: i18n.global.t('Type') },
  { id: 'AREAOFAPPLICATION', index: 10, name: i18n.global.t('Area of application') },
  { id: 'ISSUER', index: 11, name: i18n.global.t('Issuer') },
]

export const BATTERY_PASSPORT_TREE: SDSSection[] = [
  {
    id: 'SEC_1',
    index: 1,
    // eslint-disable-next-line
    // @ts-ignore
    name: i18n.global.t('Identification'),
    items: [{ id: 'SEC_1_1', name: i18n.global.t('Product Identifier') }],
  },
  { id: 'GENERAL', index: 2, name: i18n.global.t('General') },
  { id: 'MANUFACTURER', index: 3, name: i18n.global.t('Manufacturer') },
  { id: 'MANUFACTURING_PLACE', index: 4, name: i18n.global.t('Manufacturing place') },
  { id: 'OPERATOR', index: 5, name: i18n.global.t('Operator') },
  { id: 'CARBON', index: 6, name: i18n.global.t('Carbon') },
  { id: 'CIRCULARITY', index: 7, name: i18n.global.t('Circularity') },
  { id: 'SAFETY', index: 8, name: i18n.global.t('Safety') },
  { id: 'EOL', index: 9, name: i18n.global.t('End of life') },
  { id: 'LABELS', index: 10, name: i18n.global.t('Labels') },
  { id: 'CHEMISTRY', index: 11, name: i18n.global.t('Chemistry') },
  { id: 'MATERIALS', index: 12, name: i18n.global.t('Materials') },
  { id: 'HAZARDOUS', index: 13, name: i18n.global.t('Hazardous') },
  { id: 'TECH', index: 14, name: i18n.global.t('Technical') },
  { id: 'CONDITION', index: 15, name: i18n.global.t('Condition') },
  { id: 'SUPPLY_CHAIN', index: 16, name: i18n.global.t('Supply chain') },
]

export const CUSTOM_FALLBACK_TREE: SDSSection[] = [
  {
    id: 'CUSTOM_FALLBACK_SECTION',
    index: 1,
    name: i18n.global.t('Items'),
  },
]

export const TDS_STRUCTURE = {
  name: TDS_CANONICAL_NAME,
  searchResultsItems: [],
  tree: TDS_TREE,
}

export const SDS_STRUCTURE = {
  name: SDS_CANONICAL_NAME,
  searchResultsItems: [
    SDS_CANONICAL_CAS,
    SDS_CANONICAL_PRODUCT_NUMBER,
    'SEC_1_1.REG_OK',
    'SEC_1_1.REG_NR',
    SDS_CANONICAL_MANUFACTURER,
    'SEC_1_3.EMAIL',
  ],
  tree: SDS_TREE,
}

export const MATERIAL_CLASSIFICATION_COLUMNS = [
  {
    name: i18n.global.t('HCODE'),
    canonicalPreffix: ClassificationCanonicalsSuffixesEnum.HCode,
    colClasses: 'col-1',
  },
  {
    name: i18n.global.t('Hazard statements'),
    canonicalPreffix: ClassificationCanonicalsSuffixesEnum.HazardStatement,
    colClasses: 'col-2',
  },
  {
    name: i18n.global.t('Hazard class'),
    canonicalPreffix: ClassificationCanonicalsSuffixesEnum.HazardClass,
    colClasses: 'col-2',
  },
  {
    name: i18n.global.t('Category'),
    canonicalPreffix: ClassificationCanonicalsSuffixesEnum.Category,
    colClasses: 'col-1',
  },
  {
    name: i18n.global.t('Additional information'),
    canonicalPreffix: ClassificationCanonicalsSuffixesEnum.AddInfo,
    colClasses: 'col-1',
  },
  {
    name: i18n.global.t('Organ'),
    canonicalPreffix: ClassificationCanonicalsSuffixesEnum.Organ,
    colClasses: 'col-2',
  },
  {
    name: i18n.global.t('Route of exposure'),
    canonicalPreffix: ClassificationCanonicalsSuffixesEnum.RouteOfExposure,
    colClasses: 'col-2',
  },
  {
    name: i18n.global.t('Remark'),
    canonicalPreffix: ClassificationCanonicalsSuffixesEnum.Remark,
    colClasses: 'col-1',
  },
  {
    name: i18n.global.t('Regulatory Base'),
    canonicalPreffix: ClassificationCanonicalsSuffixesEnum.RegulatoryBase,
    colClasses: 'col-1',
  },
  {
    name: i18n.global.t('Regulatory List'),
    canonicalPreffix: ClassificationCanonicalsSuffixesEnum.RegulatoryList,
    colClasses: 'col-1',
  },
]

export const COMPOSITION_REFERENCE_COLUMN_KEY = 'COMPOSITION_REFERENCE'
export const COMPOSITION_CREATE_MIXTURE_COLUMN_KEY = 'COMPOSITION_CREATE_MIXTURE'

export const MAIN_MATERIAL_COMPOSITION_COLUMNS = [
  {
    name: i18n.global.t('Reference'),
    canonicalPreffix: COMPOSITION_REFERENCE_COLUMN_KEY,
    colClasses: 'col-2',
  },
  {
    name: i18n.global.t('CAS'),
    canonicalPreffix: COMPOSITION_CAS_NUMBER_KEY,
    colClasses: 'col-1',
  },
  {
    name: i18n.global.t('Name'),
    canonicalPreffix: COMPOSITION_COMPONENT_KEY,
    colClasses: 'col-2',
  },
  {
    name: i18n.global.t('Concentration'),
    canonicalPreffix: COMPOSITION_CONCENTRATION_KEY,
    colClasses: 'col-1',
  },
  {
    name: i18n.global.t('Classification'),
    canonicalPreffix: COMPOSITION_CLASSIFICATION_KEY,
    colClasses: 'col-2',
  },
  {
    name: i18n.global.t('Hazard Statements'),
    canonicalPreffix: HAZARD_STATEMENT_PREFIX_KEY,
    colClasses: 'min-w-19rem',
  },
  {
    name: i18n.global.t('EG No.'),
    canonicalPreffix: COMPOSITION_EG_NUMBER_KEY,
    colClasses: 'col-1',
  },
  {
    name: i18n.global.t('Index Nr.'),
    canonicalPreffix: COMPOSITION_INDEX_NR_KEY,
    colClasses: 'col-1',
  },
  {
    name: '',
    canonicalPreffix: COMPOSITION_CREATE_MIXTURE_COLUMN_KEY,
    colClasses: 'col-2',
  },
]
